// ============================================================================
//  Fase 4 — Reglas de presentación del plan de acción CX
// ----------------------------------------------------------------------------
//  Funciones PURAS, sin I/O. Portadas de src/lib/rules.ts del prototipo
//  (docs/fase4/) quitando TypeScript y las clases de Tailwind.
//
//  QUÉ HACEN Y QUÉ NO. Estas funciones sirven para la EXPERIENCIA DE USUARIO:
//  pintar el semáforo, habilitar un botón, explicar por qué no se puede cerrar
//  todavía. NO son la autoridad. Las mismas reglas viven en PKG_CRM_CX_PLAN y
//  la base vuelve a validarlas en cada operación; si alguien llama a la API a
//  mano, ahí es donde se detiene.
//
//  Sin dependencias nuevas: la aritmética de fechas es JS plano, igual que en
//  portal.helpers.js.
// ============================================================================

import { DIA } from "./portal.constants";

// ── Estados del plan ────────────────────────────────────────────────────────
// Las claves y los colores replican CRM_CX_PLAN_ESTATUS (ver el seed de la
// Etapa 1). El backend también los devuelve en GET /cx/catalogos; esta tabla es
// el respaldo para pintar sin esperar al catálogo.
export const ESTATUS_PLAN = {
  borrador:             { label: "Borrador",                color: "#64748b" },
  pendiente_asignacion: { label: "Pendiente de asignación",  color: "#475569" },
  asignado:             { label: "Asignado",                 color: "#0284c7" },
  pendiente_aceptacion: { label: "Pendiente de aceptación",  color: "#7c3aed" },
  aceptado:             { label: "Aceptado",                 color: "#0284c7" },
  en_analisis:          { label: "En análisis",              color: "#0891b2" },
  plan_definido:        { label: "Plan definido",            color: "#0891b2" },
  en_implementacion:    { label: "En proceso",               color: "#2563eb" },
  en_validacion:        { label: "En validación",            color: "#4f46e5" },
  con_observaciones:    { label: "Con observaciones",        color: "#d97706" },
  vencido:              { label: "Vencido",                  color: "#dc2626" },
  reabierto:            { label: "Reabierto",                color: "#e11d48" },
  concluido:            { label: "Concluido",                color: "#16a34a" },
  cancelado:            { label: "Cancelado",                color: "#94a3b8" },
};

// Estados terminales: ni vencen ni admiten seguimiento.
export const ESTATUS_FINALES = ["concluido", "cancelado"];

export const estatusLabel = (cve) =>
  (ESTATUS_PLAN[cve] && ESTATUS_PLAN[cve].label) || cve || "—";

export const estatusColor = (cve) =>
  (ESTATUS_PLAN[cve] && ESTATUS_PLAN[cve].color) || "#64748b";

// ── Semáforo ────────────────────────────────────────────────────────────────
// Sección 6 del requerimiento: se deriva de la fecha compromiso VIGENTE y del
// estado. Ojo: usa la vigente, no la original — la original solo se conserva
// como evidencia del compromiso inicial (RN-05).
export const SEM = {
  verde:    { key: "verde",    label: "En tiempo",       color: "#16a34a" },
  amarillo: { key: "amarillo", label: "Faltan ≤ 5 días", color: "#eab308" },
  naranja:  { key: "naranja",  label: "Faltan ≤ 2 días", color: "#ea580c" },
  rojo:     { key: "rojo",     label: "Vencido",         color: "#dc2626" },
  gris:     { key: "gris",     label: "Cerrado",         color: "#94a3b8" },
};

export function semaforo(plan) {
  if (ESTATUS_FINALES.indexOf(plan.estatus) !== -1) return SEM.gris;
  const d = diasRestantes(plan.fechaCompromiso);
  if (plan.estatus === "vencido" || d < 0) return SEM.rojo;
  if (d <= 2) return SEM.naranja;
  if (d <= 5) return SEM.amarillo;
  return SEM.verde;
}

// Días de calendario entre hoy y la fecha ISO dada. Negativo = ya pasó.
// Se comparan días completos, no instantes: la hora no debe alterar el conteo.
export function diasRestantes(iso) {
  if (!iso) return Number.POSITIVE_INFINITY;
  const d = new Date(iso + "T12:00:00");
  if (isNaN(d)) return Number.POSITIVE_INFINITY;
  const hoy = new Date();
  const a = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
  const b = Date.UTC(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
  return Math.round((a - b) / DIA);
}

// ── Fechas ──────────────────────────────────────────────────────────────────
const MESES = ["ene", "feb", "mar", "abr", "may", "jun",
               "jul", "ago", "sep", "oct", "nov", "dic"];

// "2026-03-05" → "5 mar 2026". Sin toLocaleDateString: su salida cambia con la
// versión de ICU del navegador y aquí necesitamos un formato estable.
export function fmtFecha(iso) {
  if (!iso) return "—";
  const d = new Date(iso.length === 10 ? iso + "T12:00:00" : iso);
  if (isNaN(d)) return "—";
  return d.getDate() + " " + MESES[d.getMonth()] + " " + d.getFullYear();
}

// ── Días hábiles (RN-16) ────────────────────────────────────────────────────
// Los escalamientos se cuentan en días hábiles, no naturales. El calendario de
// días festivos queda para la Fase 2; hoy solo se excluyen sábado y domingo.
const esFinDeSemana = (d) => d.getDay() === 0 || d.getDay() === 6;

export function sumaDiasHabiles(desde, n) {
  const d = new Date(desde.getTime());
  let sumados = 0;
  while (sumados < n) {
    d.setDate(d.getDate() + 1);
    if (!esFinDeSemana(d)) sumados++;
  }
  return d;
}

export function diasHabilesEntre(desde, hasta) {
  const d = new Date(desde.getTime());
  let n = 0;
  while (d < hasta) {
    d.setDate(d.getDate() + 1);
    if (!esFinDeSemana(d)) n++;
  }
  return n;
}

// ── Condiciones de cierre (RN-08/09) ────────────────────────────────────────
// Devuelve TODO lo que falta, no solo lo primero, para que el usuario lo
// resuelva de una vez. PKG_CRM_CX_PLAN.p_valida_cierre repite esta comprobación
// en la base y arma el mismo mensaje.
export function puedeCerrar(plan) {
  const faltan = [];
  const tareas = plan.tareas || [];
  const evidencias = plan.evidencias || [];

  if ((plan.avance || 0) < 100) {
    faltan.push("El avance debe ser 100%");
  }
  if (tareas.some((t) => ["concluida", "cancelada"].indexOf(t.estatus) === -1)) {
    faltan.push("Hay actividades pendientes");
  }
  if (!evidencias.some((e) => e.estatus === "aprobada")) {
    faltan.push("Se requiere al menos una evidencia aprobada");
  }
  if (plan.estatus !== "en_validacion" && plan.estatus !== "vencido") {
    faltan.push("El plan debe estar en validación");
  }
  return { ok: faltan.length === 0, faltan };
}

// RN-08: enviar a validación exige al menos una evidencia cargada (cualquier
// estatus). Es una condición más laxa que la de cierre, a propósito: primero se
// carga la evidencia, luego CX la aprueba.
export function puedeEnviarAValidacion(plan) {
  const faltan = [];
  if ((plan.evidencias || []).length === 0) {
    faltan.push("Carga al menos una evidencia");
  }
  if (["en_implementacion", "con_observaciones", "vencido"].indexOf(plan.estatus) === -1) {
    faltan.push("El plan debe estar en implementación");
  }
  return { ok: faltan.length === 0, faltan };
}

// ── Permisos de presentación ────────────────────────────────────────────────
// Espejo de las validaciones de rol del paquete, para ocultar o deshabilitar
// botones. No sustituye la autorización: la decide el backend.
export const esCX = (rol) =>
  rol === "cx" || rol === "admin" || rol === "direccion";

export const esGerenteArea = (rol) => (rol || "") === "gerente_area";

export function accionesDisponibles(plan, sesion) {
  const rol = (sesion && sesion.rol) || "";
  const esResponsable = !!sesion && plan.idResponsable === sesion.idColaborador;
  const cx = esCX(rol);
  const e = plan.estatus;

  // El gerente de área no participa del flujo de planes: solo ve incidencias.
  if (rol === "gerente_area") {
    return {
      asignar: false, aceptar: false, rechazar: false, causaRaiz: false,
      agregarTarea: false, agregarEvidencia: false, enviarValidacion: false,
      revisarEvidencia: false, observaciones: false, cerrar: false,
      reabrir: false, cambiarFecha: false, comentar: false,
    };
  }

  return {
    // Designar responsable es de CX: el plan nace asignado solo a un área.
    asignar:          e === "pendiente_asignacion" && cx,
    aceptar:          e === "pendiente_aceptacion" && (esResponsable || rol === "admin"),
    rechazar:         e === "pendiente_aceptacion" && (esResponsable || rol === "admin"),
    causaRaiz:        ["aceptado", "en_analisis"].indexOf(e) !== -1 && (esResponsable || cx),
    agregarTarea:     ["en_analisis", "plan_definido", "en_implementacion", "con_observaciones",
                       "reabierto", "vencido"].indexOf(e) !== -1 && (esResponsable || cx),
    agregarEvidencia: ESTATUS_FINALES.indexOf(e) === -1 && (esResponsable || cx),
    enviarValidacion: puedeEnviarAValidacion(plan).ok && (esResponsable || rol === "admin"),
    revisarEvidencia: e === "en_validacion" && cx,
    observaciones:    e === "en_validacion" && cx,
    cerrar:           puedeCerrar(plan).ok && cx,
    reabrir:          e === "concluido" && cx,
    cambiarFecha:     ESTATUS_FINALES.indexOf(e) === -1 && (esResponsable || cx),
    comentar:         true,
  };
}
